type BlogsData = {
  blogs: Record<
    string,
    {
      languages: Record<string, string>
    }
  >
}

const languagesList = ['en', 'es', 'pt', 'th', 'ko', 'zh-hans', 'zh-hant', 'jp']

let cachedData: BlogsData | null = null
let isLoading = false
let loadPromise: Promise<void> | null = null

async function initSeoSlugs(): Promise<void> {
  if (cachedData || isLoading) return loadPromise || Promise.resolve()

  isLoading = true
  loadPromise = (async () => {
    try {
      const url = import.meta.env.VITE_SEO_SLUGS_CONFIG_URL

      if (!url) {
        console.warn('VITE_SEO_SLUGS_CONFIG_URL is not defined, using empty data')
        cachedData = { blogs: {} }
        return
      }

      // Cache-busting: append timestamp to force fresh fetch
      const separator = url.includes('?') ? '&' : '?'
      const cacheBustedUrl = `${url}${separator}v=${Date.now()}`

      const response = await fetch(cacheBustedUrl)

      if (!response.ok) {
        throw new Error(`Failed to fetch SEO slugs: ${response.status}`)
      }

      const data = await response.json()
      cachedData = data
    } catch (error) {
      console.error('Error fetching SEO slugs:', error)
      cachedData = { blogs: {} }
    } finally {
      isLoading = false
    }
  })()

  return loadPromise
}

function getSeoSlugsSync(): BlogsData {
  if (!cachedData) {
    console.warn('SEO slugs not loaded yet, returning empty data')
    return { blogs: {} }
  }
  return cachedData
}

function buildCmsRoutesMap(data: BlogsData) {
  const cmsRoutesMap = new Map()

  for (const [, { languages }] of Object.entries(data.blogs)) {
    const set = new Set<string>()
    const routes: Record<string, string> = {}

    languagesList.forEach((lang) => {
      const path = languages[lang]

      if (path) {
        set.add(path)
        routes[lang] = path
      } else {
        const url = lang === 'en' ? '/404/' : `/${lang}/404/`
        set.add(url)
        routes[lang] = url
      }
    })

    cmsRoutesMap.set(set, routes)
  }

  return cmsRoutesMap
}

export function findCmsRoutesPath(path: string) {
  const normalizePath = (value: string) => {
    let normalized = value.trim()
    if (!normalized.startsWith('/')) normalized = `/${normalized}`
    normalized = normalized.replace(/\/{2,}/g, '/')
    normalized = normalized.endsWith('/') ? normalized : `${normalized}/`
    if (normalized.startsWith('/en/')) normalized = normalized.replace(/^\/en\//, '/')
    try {
      normalized = decodeURIComponent(normalized)
    } catch {
      // keep original when decoding fails
    }
    return normalized
  }

  const data = getSeoSlugsSync()
  const cmsRoutesMap = buildCmsRoutesMap(data)
  const normPath = normalizePath(path)

  for (const [, routes] of cmsRoutesMap) {
    const match = Object.values(routes).some((routePath: any) => normalizePath(routePath) === normPath)
    if (match) return routes
  }

  const slugMatch = normPath.match(/\/blog\/([^/]+)\//)
  if (slugMatch) {
    let targetSlug = slugMatch[1]
    try {
      targetSlug = decodeURIComponent(targetSlug)
    } catch {
    }

    for (const [, routes] of cmsRoutesMap) {
      const slugMatchInRoutes = Object.values(routes).some((routePath: any) => {
        const normalizedRoute = normalizePath(routePath)
        const routeSlug = normalizedRoute
          .replace(/^\/(en|es|pt|th|ko|zh-hans|zh-hant|jp)?\/blog\//, '')
          .replace(/\/$/, '')
        return routeSlug === targetSlug
      })
      if (slugMatchInRoutes) return routes
    }
  }

  return null
}

export function convertToEnglishSlug(slug: string) {
  const data = getSeoSlugsSync()
  const cmsRoutesMap = buildCmsRoutesMap(data)

  for (const [pathSet, routes] of cmsRoutesMap) {
    const enPath = routes.en
    const enIsValid = enPath && enPath !== '/404/'

    const enSlug = enIsValid ? enPath.replace(/^\/blog\//, '').replace(/\/$/, '') : null

    if (enSlug === slug) {
      return slug
    }

    for (const path of pathSet) {
      const pathSlug = path
        .replace(/^\/(en|ko|es|pt|th|zh-hans|zh-hant|jp)?\/blog\//, '')
        .replace(/\/$/, '')

      if (pathSlug === slug) {
        return enSlug ?? slug
      }
    }
  }
  return null
}

export async function preloadSeoSlugs() {
  await initSeoSlugs()
}
